import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheck,
  faCheckDouble,
  faClipboard,
  faClock,
  faUser,
  faGlobe,
  faServer,
  faFingerprint,
  faLayerGroup,
  faStickyNote,
} from "@fortawesome/free-solid-svg-icons";
import "./css/SystemErrors.css";

/**
 * Modal for viewing full error details, including stack trace,
 * request context, and acknowledge/resolve workflow.
 */
function ErrorDetailModal({
  error,
  onClose,
  onAcknowledge,
  onResolve,
  categoryConfig,
  severityConfig,
  statusConfig,
}) {
  const [notes, setNotes] = useState(error?.notes || "");
  const [showResolveForm, setShowResolveForm] = useState(false);

  if (!error) return null;

  const catConfig =
    categoryConfig[error.category] || categoryConfig.INFRASTRUCTURE;
  const sevConfig = severityConfig[error.severity] || severityConfig.LOW;
  const statConfig = statusConfig[error.status] || statusConfig.NEW;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  return (
    <div className="error-detail-overlay" onClick={onClose}>
      <div className="error-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <div className="modal-badges">
              <span
                className="severity-badge large"
                style={{
                  color: sevConfig.color,
                  backgroundColor: sevConfig.bgColor,
                }}
              >
                {sevConfig.label}
              </span>
              <span
                className="category-badge"
                style={{ color: catConfig.color }}
              >
                <FontAwesomeIcon icon={catConfig.icon} />
                {catConfig.label}
              </span>
              <span
                className="status-badge"
                style={{ color: statConfig.color }}
              >
                <FontAwesomeIcon icon={statConfig.icon} />
                {statConfig.label}
              </span>
            </div>
            <h2>{error.errorType || "Error Detail"}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Error Message */}
          <div className="detail-section">
            <h3>Error Message</h3>
            <div className="error-message-block">
              <pre>{error.message}</pre>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(error.message)}
                title="Copy message"
              >
                <FontAwesomeIcon icon={faClipboard} />
              </button>
            </div>
          </div>

          {/* Stack Trace */}
          {error.stackTrace && (
            <div className="detail-section">
              <h3>Stack Trace</h3>
              <div className="stack-trace-block">
                <pre>{error.stackTrace}</pre>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(error.stackTrace)}
                  title="Copy stack trace"
                >
                  <FontAwesomeIcon icon={faClipboard} />
                </button>
              </div>
            </div>
          )}

          {/* Request Context */}
          <div className="detail-section">
            <h3>Context</h3>
            <div className="context-grid">
              <div className="context-item">
                <FontAwesomeIcon icon={faClock} />
                <span className="context-label">Timestamp</span>
                <span className="context-value">
                  {formatTimestamp(error.timestamp)}
                </span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faServer} />
                <span className="context-label">Source Service</span>
                <span className="context-value">
                  {error.sourceService || "—"}
                </span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faGlobe} />
                <span className="context-label">Endpoint</span>
                <span className="context-value">
                  {error.httpMethod && error.endpoint
                    ? `${error.httpMethod} ${error.endpoint}`
                    : error.endpoint || "—"}
                </span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faUser} />
                <span className="context-label">User ID</span>
                <span className="context-value">{error.userId || "—"}</span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faGlobe} />
                <span className="context-label">Client IP</span>
                <span className="context-value">{error.clientIp || "—"}</span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faFingerprint} />
                <span className="context-label">Request ID</span>
                <span className="context-value">{error.requestId || "—"}</span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faLayerGroup} />
                <span className="context-label">Occurrences</span>
                <span className="context-value">
                  {error.occurrenceCount || 1}
                </span>
              </div>
              <div className="context-item">
                <FontAwesomeIcon icon={faFingerprint} />
                <span className="context-label">Fingerprint</span>
                <span className="context-value mono">
                  {error.fingerprint || "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Log Source */}
          <div className="detail-section">
            <h3>Log Source</h3>
            <div className="context-grid compact">
              <div className="context-item">
                <span className="context-label">Log Group</span>
                <span className="context-value mono">
                  {error.logGroup || "—"}
                </span>
              </div>
              <div className="context-item">
                <span className="context-label">Log Stream</span>
                <span className="context-value mono">
                  {error.logStream || "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Audit Trail */}
          {(error.acknowledgedBy || error.resolvedBy) && (
            <div className="detail-section">
              <h3>Audit Trail</h3>
              <div className="audit-trail">
                {error.acknowledgedBy && (
                  <div className="audit-entry">
                    <span className="audit-action">Acknowledged</span>
                    <span className="audit-by">by {error.acknowledgedBy}</span>
                    <span className="audit-time">
                      {formatTimestamp(error.acknowledgedAt)}
                    </span>
                  </div>
                )}
                {error.resolvedBy && (
                  <div className="audit-entry">
                    <span className="audit-action resolved">Resolved</span>
                    <span className="audit-by">by {error.resolvedBy}</span>
                    <span className="audit-time">
                      {formatTimestamp(error.resolvedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes / Resolution Form */}
          {showResolveForm && (
            <div className="detail-section">
              <h3>
                <FontAwesomeIcon icon={faStickyNote} /> Resolution Notes
              </h3>
              <textarea
                className="resolution-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe the resolution or root cause..."
                rows={4}
              />
            </div>
          )}

          {error.notes && !showResolveForm && (
            <div className="detail-section">
              <h3>Notes</h3>
              <div className="notes-block">{error.notes}</div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="modal-footer">
          <div className="footer-left">
            <span className="error-id">ID: {error.errorId}</span>
          </div>
          <div className="footer-actions">
            {error.status === "NEW" && (
              <button
                className="action-btn-modal acknowledge"
                onClick={() => onAcknowledge(error.category, error.errorId)}
              >
                <FontAwesomeIcon icon={faCheck} />
                Acknowledge
              </button>
            )}
            {error.status !== "RESOLVED" && !showResolveForm && (
              <button
                className="action-btn-modal resolve"
                onClick={() => setShowResolveForm(true)}
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                Resolve
              </button>
            )}
            {showResolveForm && (
              <button
                className="action-btn-modal resolve confirm"
                onClick={() => onResolve(error.category, error.errorId, notes)}
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                Confirm Resolution
              </button>
            )}
            <button className="action-btn-modal close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ErrorDetailModal;
